import { Fragment, useState } from "react";
import { Check, ChevronDown, ChevronRight, ChevronsUpDown, Sprout } from "lucide-react";
import { usePagedAvailableGreenhouseStock } from "../hooks/useGreenhouseStock";
import { StockSowingDate } from "../components/greenhouseStock/StockSowingDate";
import { useActiveCropTypes } from "../hooks/useCropTypes";
import { useCropVarietiesByCropType } from "../hooks/useCropVarieties";
import { useActiveVarietyPricings } from "../hooks/useVarietyPricings";
import { useExpos } from "@/features/expos/hooks/useExpos";
import { useCreateExpoTransfer } from "@/features/expos/hooks/useExpoTransfers";
import type { ExpoStatus } from "@/features/expos/services/exposApi";
import type { VarietyPricing } from "../services/varietyPricingApi";
import { useAuthStore } from "@/features/auth/stores/authStore";
import {
  ReleaseReserveDialog,
  type ReleaseReserveDialogEntry,
} from "../components/greenhouseStock/ReleaseReserveDialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Field, FieldLabel } from "@/components/ui/field";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { format, parseISO } from "date-fns";
import { PaginationBar } from "@/components/ui/PaginationBar";
import type { AvailableGreenhouseStockDto } from "../services/sowingInstructionsApi";

const TRAY_TYPES = [
  { value: "all", label: "All Tray Types" },
  { value: "210", label: "210-hole" },
  { value: "442", label: "442-hole" },
];

const isValidDecimalInput = (value: string) => /^\d*(?:\.\d*)?$/.test(value);
const ELIGIBLE_EXPO_STATUSES = new Set<ExpoStatus>(["Approved", "Open", "Active"]);

const parsePositiveDecimal = (value: string) => {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
};

const normalize = (value?: string | null) => (value ?? "").trim().toLowerCase();
const parseTrayType = (value?: string | number | null): number | null => {
  if (value === 210 || value === 442) return value;
  const normalized = String(value ?? "").trim().toLowerCase();
  if (!normalized) return null;
  if (normalized.includes("210") || normalized.includes("twoten") || normalized.includes("two ten")) return 210;
  if (normalized.includes("442") || normalized.includes("fourfortytwo") || normalized.includes("four forty two")) return 442;
  return null;
};

const configuredPriceFor = (entry: AvailableGreenhouseStockDto, pricings: VarietyPricing[]) => {
  const pricingMode = entry.trayTypeInt == null ? entry.pricingMode : 1;
  const pricing = pricings.find(
    (price) =>
      normalize(price.cropTypeName) === normalize(entry.cropTypeName) &&
      normalize(price.varietyName) === normalize(entry.variety) &&
      price.pricingMode === pricingMode &&
      (pricingMode === 1 ? parseTrayType(price.trayType) === (entry.trayTypeInt ?? null) : price.trayType == null) &&
      price.companySeedlingPrice != null &&
      price.companySeedlingPrice >= 0,
  );
  return pricing?.companySeedlingPrice ?? 0;
};

// Shared filter bar + table used by both tabs
const StockTable = ({
  entries,
  showSeedlingColumn,
  canReleaseReserve,
  canRequestExpoMove,
  onReleaseReserve,
  onMoveToExpo,
}: {
  entries: AvailableGreenhouseStockDto[];
  showSeedlingColumn: boolean;
  canReleaseReserve: boolean;
  canRequestExpoMove: boolean;
  onReleaseReserve: (entry: AvailableGreenhouseStockDto) => void;
  onMoveToExpo: (entry: AvailableGreenhouseStockDto) => void;
}) => {
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const toggleExpand = (entryId: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(entryId)) next.delete(entryId);
      else next.add(entryId);
      return next;
    });
  };

  const colSpan = 14 + (showSeedlingColumn ? 1 : 0) + (canReleaseReserve || canRequestExpoMove ? 1 : 0);

  return (
    <div className="max-w-full overflow-hidden rounded-lg border bg-card">
      <div className="overflow-x-auto">
    <Table className="min-w-[1360px]">
      <TableHeader>
        <TableRow>
          <TableHead className="w-[150px] whitespace-nowrap">Reservations</TableHead>
          <TableHead className="whitespace-nowrap">Crop Type</TableHead>
          <TableHead className="min-w-[220px]">Variety</TableHead>
          <TableHead className="whitespace-nowrap">Customer</TableHead>
          <TableHead className="whitespace-nowrap">Tray Type</TableHead>
          <TableHead className="whitespace-nowrap">Available Trays</TableHead>
          {showSeedlingColumn && <TableHead className="whitespace-nowrap">Sellable Seedlings</TableHead>}
          <TableHead className="whitespace-nowrap">Reserved</TableHead>
          <TableHead className="whitespace-nowrap">Filling Reserve</TableHead>
          <TableHead className="whitespace-nowrap">Greenhouse</TableHead>
          <TableHead className="whitespace-nowrap">Chamber</TableHead>
          <TableHead className="whitespace-nowrap">Sowing Date</TableHead>
          <TableHead className="whitespace-nowrap">Entry Date</TableHead>
          <TableHead className="whitespace-nowrap">Expected Ready</TableHead>
          {(canReleaseReserve || canRequestExpoMove) && <TableHead className="sticky right-0 z-10 bg-muted/50 text-right whitespace-nowrap shadow-[-8px_0_12px_-12px_rgba(0,0,0,0.35)]">Actions</TableHead>}
        </TableRow>
      </TableHeader>
      <TableBody>
        {entries.map((entry) => {
          const reservationOrders = entry.reservationOrders ?? [];
          const hasReservations = reservationOrders.length > 0;
          const isExpanded = expandedRows.has(entry.entryId);

          return (
          <Fragment key={entry.entryId}>
          <TableRow>
            <TableCell className="whitespace-nowrap">
              {hasReservations ? (
                <button
                  type="button"
                  onClick={() => toggleExpand(entry.entryId)}
                  className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
                >
                  {isExpanded
                    ? <ChevronDown className="h-3.5 w-3.5" />
                    : <ChevronRight className="h-3.5 w-3.5" />}
                  {reservationOrders.length} order{reservationOrders.length !== 1 ? "s" : ""}
                </button>
              ) : (
                <span className="text-xs text-muted-foreground">No active reservations</span>
              )}
            </TableCell>
            <TableCell className="whitespace-nowrap">{entry.cropTypeName}</TableCell>
            <TableCell className="max-w-[260px] truncate" title={entry.variety}>{entry.variety}</TableCell>
            <TableCell className="whitespace-nowrap text-sm">
              {entry.customerName === "Standalone" ? "Agitech" : entry.customerName}
            </TableCell>
            <TableCell className="whitespace-nowrap">
              <Badge variant="outline">{entry.trayType ?? "per-seedling"}</Badge>
            </TableCell>
            <TableCell className="whitespace-nowrap font-semibold">
              <span>{entry.availableQuantity}</span>
              {entry.pendingDispatchQuantity > 0 && (
                <span
                  className="ml-2 inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800 ring-1 ring-amber-300"
                  title="These trays are committed to an in-progress dispatch (initiated but not yet handed over)"
                >
                  {entry.pendingDispatchQuantity} dispatching
                </span>
              )}
            </TableCell>
            {showSeedlingColumn && (
              <TableCell className="whitespace-nowrap">
                {entry.sellablePerTray != null ? (
                  <span className="font-semibold text-emerald-700">
                    {entry.availableSeedlings.toLocaleString()}
                    <span className="text-xs text-muted-foreground ml-1">
                      ({entry.sellablePerTray}/tray · {(entry.trayTypeInt ?? 0) - entry.sellablePerTray} buf)
                    </span>
                  </span>
                ) : (
                  <span className="font-semibold text-emerald-700">
                    {entry.availableSeedlings.toLocaleString()}
                  </span>
                )}
              </TableCell>
            )}
            <TableCell className="whitespace-nowrap text-muted-foreground">
              {entry.reservedQuantity > 0 ? (
                <Badge variant="secondary">{entry.reservedQuantity}</Badge>
              ) : "—"}
            </TableCell>
            <TableCell className="whitespace-nowrap text-muted-foreground">
              {entry.fillingReserve > 0 ? (
                <Badge className="border-amber-300 bg-amber-50 text-amber-800">
                  {entry.fillingReserve}
                </Badge>
              ) : "—"}
            </TableCell>
            <TableCell className="whitespace-nowrap text-muted-foreground">{entry.greenhouseName ?? "—"}</TableCell>
            <TableCell className="whitespace-nowrap text-muted-foreground">{entry.chamberName ?? "—"}</TableCell>
            <TableCell className="whitespace-nowrap text-sm">
              <StockSowingDate value={entry.sowingDate} source={entry.source} />
            </TableCell>
            <TableCell className="whitespace-nowrap text-sm">
              {entry.entryDate ? format(parseISO(entry.entryDate), "dd MMM yyyy") : "—"}
            </TableCell>
            <TableCell className="whitespace-nowrap text-sm">
              {entry.expectedReadyDate ? format(parseISO(entry.expectedReadyDate), "dd MMM yyyy") : "—"}
            </TableCell>
            {(canReleaseReserve || canRequestExpoMove) && (
              <TableCell className="sticky right-0 z-10 bg-card text-right shadow-[-8px_0_12px_-12px_rgba(0,0,0,0.35)]">
                <div className="flex justify-end gap-2 whitespace-nowrap">
                {entry.fillingReserve > 0 && (
                  <Button size="sm" variant="outline" onClick={() => onReleaseReserve(entry)}>
                    Release Reserve
                  </Button>
                )}
                {canRequestExpoMove && entry.availableQuantity > 0 && (
                  <Button size="sm" variant="outline" onClick={() => onMoveToExpo(entry)}>
                    Move to Expo
                  </Button>
                )}
                </div>
              </TableCell>
            )}
          </TableRow>

          {hasReservations && isExpanded && (
            <TableRow>
              <TableCell colSpan={colSpan} className="bg-muted/20">
                <div className="rounded-md border bg-background p-3 space-y-2">
                  <p className="text-xs font-medium text-muted-foreground">Orders reserving this entry</p>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Order #</TableHead>
                        <TableHead>Customer</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">Reserved Qty</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {reservationOrders.map((reservation) => (
                        <TableRow key={reservation.orderId}>
                          <TableCell className="font-mono text-xs">{reservation.orderNumber}</TableCell>
                          <TableCell>{reservation.customerName}</TableCell>
                          <TableCell>
                            <Badge variant="outline">{reservation.orderStatus}</Badge>
                          </TableCell>
                          <TableCell className="text-right font-medium">{reservation.reservedQuantity}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </TableCell>
            </TableRow>
          )}
          </Fragment>
        )})}
      </TableBody>
    </Table>
      </div>
    </div>
  );
};

export const GreenhouseStockEntriesPage = () => {
  const [activeTab, setActiveTab] = useState<"all" | "ready">("all");
  const [cropTypeId, setCropTypeId] = useState("");
  const [cropTypeOpen, setCropTypeOpen] = useState(false);
  const [variety, setVariety] = useState("");
  const [varietyOpen, setVarietyOpen] = useState(false);
  const [trayType, setTrayType] = useState("");
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [releaseEntry, setReleaseEntry] = useState<ReleaseReserveDialogEntry | null>(null);
  const [moveEntry, setMoveEntry] = useState<AvailableGreenhouseStockDto | null>(null);
  const [moveExpoId, setMoveExpoId] = useState("");
  const [moveQuantity, setMoveQuantity] = useState("");
  const [moveReason, setMoveReason] = useState("");
  const [moveFormError, setMoveFormError] = useState<string | null>(null);

  const { hasPermission } = useAuthStore();
  const canReleaseReserve = hasPermission("greenhouse_stock.request_reserve_release");
  const canRequestExpoMove = hasPermission("expo_transfers.create");
  const createExpoTransfer = useCreateExpoTransfer();

  const cropTypesQuery = useActiveCropTypes();
  const varietiesQuery = useCropVarietiesByCropType(cropTypeId);
  const exposQuery = useExpos();
  const pricingsQuery = useActiveVarietyPricings();
  const eligibleExpos = (exposQuery.data ?? [])
    .filter((expo) => ELIGIBLE_EXPO_STATUSES.has(expo.status))
    .sort((a, b) => a.startDate.localeCompare(b.startDate));

  const openReleaseDialog = (entry: AvailableGreenhouseStockDto) => {
    setReleaseEntry({
      entryId: entry.entryId,
      cropTypeName: entry.cropTypeName,
      variety: entry.variety,
      fillingReserve: entry.fillingReserve,
      stockUnit: entry.stockUnit,
    });
  };

  const handleMoveToExpo = (entry: AvailableGreenhouseStockDto) => {
    setMoveEntry(entry);
    setMoveExpoId("");
    setMoveQuantity("");
    setMoveReason("");
    setMoveFormError(null);
  };

  const closeMoveDialog = (open: boolean) => {
    if (open) return;
    if (createExpoTransfer.isPending) return;
    setMoveEntry(null);
    setMoveExpoId("");
    setMoveQuantity("");
    setMoveReason("");
    setMoveFormError(null);
  };

  const submitMoveToExpo = async () => {
    if (!moveEntry) return;

    const quantity = parsePositiveDecimal(moveQuantity);
    const maxAllowed = moveEntry.availableQuantity;
    const reason = moveReason.trim();

    if (!moveExpoId) {
      setMoveFormError("Select the Expo this stock is moving to.");
      return;
    }
    if (quantity == null || quantity <= 0) {
      setMoveFormError("Enter a valid quantity greater than zero.");
      return;
    }
    if (quantity > maxAllowed) {
      setMoveFormError(`Quantity exceeds max allowed (${maxAllowed}).`);
      return;
    }
    if (!reason) {
      setMoveFormError("Reason is required.");
      return;
    }
    if (configuredPriceFor(moveEntry, pricingsQuery.data ?? []) <= 0) {
      setMoveFormError("Expected price is not configured for this crop/variety. Set up pricing before moving stock to Expo.");
      return;
    }

    setMoveFormError(null);
    await createExpoTransfer.mutateAsync({
      expoId: moveExpoId,
      transferType: "ToExpo",
      transferDate: new Date().toISOString(),
      reason,
      remarks: null,
      lines: [
        {
          greenhouseStockEntryId: moveEntry.entryId,
          quantity,
          expectedSellingPrice: configuredPriceFor(moveEntry, pricingsQuery.data ?? []),
          remarks: null,
        },
      ],
    });
    closeMoveDialog(false);
  };

  const commonParams = {
    source: "Greenhouse" as const,
    cropTypeId: cropTypeId || undefined,
    variety: variety || undefined,
    trayType: trayType ? parseInt(trayType) : undefined,
    pageNumber,
    pageSize,
  };

  const allEntriesQuery = usePagedAvailableGreenhouseStock({
    ...commonParams,
    enabled: activeTab === "all",
  });
  const readyEntriesQuery = usePagedAvailableGreenhouseStock({
    ...commonParams,
    readyForWalkIn: true,
    enabled: activeTab === "ready",
  });

  const handleFilterChange = (setter: (v: string) => void, value: string) => {
    setter(value);
    setPageNumber(1);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setPageNumber(1);
  };

  const activeQuery = activeTab === "ready" ? readyEntriesQuery : allEntriesQuery;
  const data = activeQuery.data;
  const parsedMoveQuantity = parsePositiveDecimal(moveQuantity);
  const moveQuantityMax = moveEntry?.availableQuantity ?? 0;
  const moveQuantityError = moveEntry && moveQuantity.trim()
    ? parsedMoveQuantity == null || parsedMoveQuantity <= 0
      ? "Enter a valid quantity greater than zero."
      : parsedMoveQuantity > moveQuantityMax
        ? `Quantity exceeds available stock (${moveQuantityMax}).`
        : null
    : null;
  const canSubmitMove =
    !!moveEntry &&
    !createExpoTransfer.isPending &&
    !!moveExpoId &&
    parsedMoveQuantity != null &&
    parsedMoveQuantity > 0 &&
    parsedMoveQuantity <= moveQuantityMax &&
    !!moveReason.trim();

  return (
    <div className="min-w-0 space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Greenhouse Stock</h1>
        <p className="text-muted-foreground mt-1">
          In-greenhouse batches by crop variety.
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v as "all" | "ready"); setPageNumber(1); }}>
        <TabsList>
          <TabsTrigger value="all">All In-Greenhouse</TabsTrigger>
          <TabsTrigger value="ready" className="gap-1">
            <Sprout className="h-4 w-4" />
            Ready for Sale
            {activeTab === "ready" && readyEntriesQuery.data && readyEntriesQuery.data.totalCount > 0 && (
              <span className="ml-1 rounded-full bg-emerald-100 text-emerald-800 px-1.5 text-xs font-medium">
                {readyEntriesQuery.data.totalCount}
              </span>
            )}
          </TabsTrigger>
        </TabsList>

        {activeTab === "ready" && (
          <p className="text-sm text-muted-foreground mt-2">
            Batches confirmed as <strong>ReadyForDispatch</strong> by the Growing Supervisor — these can be sold to walk-in customers.
            Seedling counts shown using per-tray buffer (442-hole: 400 sellable / 42 buffer; 210-hole: 200 sellable / 10 buffer).
          </p>
        )}

        {/* Filters — shared across both tabs */}
        <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-[minmax(12rem,13rem)_minmax(12rem,13rem)_minmax(11rem,12rem)]">
        <Field>
          <FieldLabel>Crop Type</FieldLabel>
          <Popover open={cropTypeOpen} onOpenChange={setCropTypeOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                aria-expanded={cropTypeOpen}
                className="w-full justify-between font-normal"
              >
                {cropTypeId
                  ? (cropTypesQuery.data ?? []).find((ct) => ct.id === cropTypeId)?.name
                  : "All crop types"}
                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[--radix-popover-trigger-width] p-0">
              <Command>
                <CommandInput placeholder="Search crop types..." />
                <CommandList>
                  <CommandEmpty>No crop type found.</CommandEmpty>
                  <CommandGroup>
                    <CommandItem
                      value="all"
                      onSelect={() => { handleFilterChange(setCropTypeId, ""); setVariety(""); setCropTypeOpen(false); }}>
                      <Check className={cn("mr-2 h-4 w-4", !cropTypeId ? "opacity-100" : "opacity-0")} />
                      All crop types
                    </CommandItem>
                    {(cropTypesQuery.data ?? []).map((ct) => (
                      <CommandItem
                        key={ct.id}
                        value={ct.name}
                        onSelect={() => { handleFilterChange(setCropTypeId, ct.id); setVariety(""); setCropTypeOpen(false); }}
                      >
                        <Check className={cn("mr-2 h-4 w-4", cropTypeId === ct.id ? "opacity-100" : "opacity-0")} />
                        {ct.name}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </Field>

        <Field>
          <FieldLabel>Variety</FieldLabel>
          <Popover open={varietyOpen} onOpenChange={setVarietyOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                aria-expanded={varietyOpen}
                disabled={!cropTypeId}
                className="w-full justify-between font-normal"
              >
                {variety || (cropTypeId ? "All varieties" : "Select crop type first")}
                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[--radix-popover-trigger-width] p-0">
              <Command>
                <CommandInput placeholder="Search varieties..." />
                <CommandList>
                  <CommandEmpty>No variety found.</CommandEmpty>
                  <CommandGroup>
                    <CommandItem
                      value="all"
                      onSelect={() => { handleFilterChange(setVariety, ""); setVarietyOpen(false); }}
                    >
                      <Check className={cn("mr-2 h-4 w-4", !variety ? "opacity-100" : "opacity-0")} />
                      All varieties
                    </CommandItem>
                    {(varietiesQuery.data ?? []).map((v) => (
                      <CommandItem
                        key={v.id}
                        value={v.name}
                        onSelect={() => { handleFilterChange(setVariety, v.name); setVarietyOpen(false); }}
                      >
                        <Check className={cn("mr-2 h-4 w-4", variety === v.name ? "opacity-100" : "opacity-0")} />
                        {v.name}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </Field>

        <Field>
          <FieldLabel>Tray Type</FieldLabel>
          <Select value={trayType || "all"} onValueChange={(v) => handleFilterChange(setTrayType, v === "all" ? "" : v)}>
            <SelectTrigger>
              <SelectValue placeholder="All tray types" />
            </SelectTrigger>
            <SelectContent>
              {TRAY_TYPES.map((t) => (
                <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
      </div>

      {activeQuery.isLoading && (
        <div className="flex items-center justify-center h-64">
          <p>Loading stock entries...</p>
        </div>
      )}

      {activeQuery.error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4">
          <p className="text-red-600">Failed to load greenhouse stock</p>
        </div>
      )}

      {!activeQuery.isLoading && (data?.items ?? []).length === 0 && (
        <div className="rounded-lg border bg-muted/40 p-8 text-center">
          <p className="text-muted-foreground">
            {activeTab === "ready"
              ? "No batches are confirmed ready for dispatch yet."
              : "No available stock entries match the current filters."}
          </p>
        </div>
      )}

      {(data?.items ?? []).length > 0 && (
        <StockTable
          entries={data!.items}
          showSeedlingColumn={activeTab === "ready"}
          canReleaseReserve={canReleaseReserve}
          canRequestExpoMove={canRequestExpoMove}
          onReleaseReserve={openReleaseDialog}
          onMoveToExpo={handleMoveToExpo}
        />
      )}

      {data && (
        <PaginationBar
          currentPage={data.currentPage}
          pageSize={data.pageSize}
          totalCount={data.totalCount}
          totalPages={data.totalPages}
          onPageChange={setPageNumber}
          onPageSizeChange={handlePageSizeChange}
        />
      )}
      </Tabs>

      {releaseEntry && (
        <ReleaseReserveDialog
          entry={releaseEntry}
          open={!!releaseEntry}
          onOpenChange={(v) => !v && setReleaseEntry(null)}
        />
      )}

      {moveEntry && (
        <Dialog open={!!moveEntry} onOpenChange={closeMoveDialog}>
          <DialogContent className="sm:max-w-2xl">
            <DialogHeader>
              <DialogTitle>Move Stock To Expo</DialogTitle>
              <DialogDescription>
                {moveEntry.cropTypeName}{moveEntry.variety ? ` - ${moveEntry.variety}` : ""}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-5">
              <div className="rounded-lg border bg-muted/30 p-3 text-sm text-muted-foreground">
                <span className="font-medium text-foreground">Available:</span> {moveEntry.availableQuantity}
                <span className="mx-2">·</span>
                <span className="font-medium text-foreground">Filling reserve:</span> {moveEntry.fillingReserve}
              </div>
              <div className="grid gap-4 md:grid-cols-2">
              <Field>
                <FieldLabel htmlFor="moveExpoId">Destination Expo *</FieldLabel>
                <Select
                  value={moveExpoId}
                  onValueChange={(value) => {
                    setMoveExpoId(value);
                    setMoveFormError(null);
                  }}
                  disabled={createExpoTransfer.isPending || exposQuery.isLoading || eligibleExpos.length === 0}
                >
                  <SelectTrigger id="moveExpoId" aria-required="true">
                    <SelectValue placeholder={exposQuery.isLoading ? "Loading expos..." : "Select active Expo"} />
                  </SelectTrigger>
                  <SelectContent>
                    {eligibleExpos.map((expo) => (
                      <SelectItem key={expo.id} value={expo.id}>
                        {expo.name} · {expo.locationName} · {expo.status}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  This transfer request will be linked to the selected Expo and will appear in that Expo's transfer notes.
                </p>
                {!exposQuery.isLoading && eligibleExpos.length === 0 && (
                  <p className="text-xs text-destructive">
                    No Approved, Open, or Active Expos are available for stock transfer.
                  </p>
                )}
              </Field>
              <Field>
                <FieldLabel htmlFor="moveQuantity">Quantity *</FieldLabel>
                <Input
                  id="moveQuantity"
                  type="text"
                  inputMode="decimal"
                  required
                  value={moveQuantity}
                  onChange={(e) => {
                    const next = e.target.value;
                    if (!isValidDecimalInput(next)) return;
                    setMoveQuantity(next);
                    setMoveFormError(null);
                  }}
                  aria-invalid={!!moveQuantityError}
                  placeholder={`Max ${moveQuantityMax}`}
                  disabled={createExpoTransfer.isPending}
                />
                <p className="text-xs text-muted-foreground">
                  Max transferable now: {moveEntry.availableQuantity}
                  {moveEntry.fillingReserve > 0 && " · Release filling reserve first if you need to transfer reserve stock."}
                </p>
                {moveQuantityError && (
                  <p className="text-xs text-destructive">{moveQuantityError}</p>
                )}
              </Field>
              </div>

              <Field>
                <FieldLabel htmlFor="moveReason">Reason *</FieldLabel>
                <Textarea
                  id="moveReason"
                  rows={3}
                  required
                  value={moveReason}
                  onChange={(e) => setMoveReason(e.target.value)}
                  placeholder="Why is this stock being moved to expo?"
                  disabled={createExpoTransfer.isPending}
                />
              </Field>

              {moveFormError && (
                <p className="text-sm text-destructive">{moveFormError}</p>
              )}
            </div>

            <DialogFooter>
              <Button
                variant="secondary"
                onClick={() => closeMoveDialog(false)}
                disabled={createExpoTransfer.isPending}
              >
                Cancel
              </Button>
              <Button onClick={submitMoveToExpo} disabled={!canSubmitMove}>
                {createExpoTransfer.isPending ? "Submitting..." : "Submit Transfer Request"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};
